import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { createSelector } from "reselect";
import { useTranslation } from "react-i18next";
import InfiniteScroll from "react-infinite-scroll-component";
import fp from "lodash/fp";

/* cores */
import convertUtcToLocal from "../../../cores/convertUtcToLocal";
import formatDuration from "../../../cores/formatDuration";

/* modules */
import { postDashboardQueryList } from "../../../modules/dashboard";

/* components */
import WidgetWrap from "./components/wrap";
import WidgetHead from "./components/head";
import MediaImage from "./components/MediaImage";
import WithSelect from "../../select/Select";
import StatusBadge from "../../statusbadge/StatusBadge";
import TooltipUserInfo from "../../tooltip/userInfo";
import HorizontalInfinity from "./components/HorizontalInfinity";
import { IconRefresh } from "@mzc-cloudplex/icons";
// const widgetValue =

import { checkIsMobile } from "./../../../utils/checkmobile";
import menuService from "@services/menuService";
const isMobile = checkIsMobile();

const RecentContents = ({ name, title, color, icon, removable, query, contentType, onMove, onDelete }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const history = useHistory();
    const { pathname } = useLocation();
    const { spaceId, projectId } = menuService.parse(pathname);
    const [isUnmounted, setIsUnmounted] = useState(false);
    const stateSelector = createSelector(
        (state) => state.project,
        (state) => state.dashboard,
        (state) => state.stage,
        (project, dashboard, stage) => ({
            activeProjectId: fp.get(`id`, project),
            [name]: fp.get(`${name}.data`, dashboard),
            pending: fp.get(`${name}.pending`, dashboard),
            user: fp.get(`user`, stage),
        }),
    );
    const state = useSelector(stateSelector);
    const user = state.user;
    const defaultSelectedOption = { label: t(`common::label::All Users`), value: `ALL` };
    const [options, setOptions] = useState([]);
    const [selectedOption, setSelectedOption] = useState(defaultSelectedOption);
    const [list, setList] = useState([]);
    const [isEmptyList, setIsEmptyList] = useState(false);
    const [limit, setLimit] = useState(20);
    const [offset, setOffset] = useState(0);
    const [count, setCount] = useState(0);
    const [contentTypeLower] = useState(contentType.toLowerCase());
    const [isLoading, setIsLoading] = useState(true);
    const [listProperty, setListProperty] = useState({
        name,
        resource: `content`,
        columns: fp.get("columns", query)
            ? fp.get("columns", query)
            : [
                  `contentId`,
                  `contentName`,
                  `contentThumbnailUrl`,
                  `contentStatus`,
                  `contentCreatedAt`,
                  `contentOwnerId`,
                  `contentOwnerName`,
                  `contentOwnerUsername`,
                  `contentType`,
              ],
        sort: {
            key: `createdAt`,
            order: `desc`,
        },
        filters: fp.get("filters", query),
    });

    useEffect(() => {
        // !fp.isEmpty(user) && getList(limit, offset, [], selectedOption);
        !fp.isEmpty(user) && handleReset({ selectedOption, projectId: state.activeProjectId });
    }, [state.activeProjectId, user]);

    const getList = useCallback(
        (limit, offset, array, selectedOption, projectId) => {
            const userIdOption = selectedOption
                ? fp.get(`value`, selectedOption)
                : fp.get(`value`, defaultSelectedOption);
            const projectId_ = projectId ? projectId : state.activeProjectId;
            const optionsMap = {
                currentProjectId: () => projectId_,
                loginUserId: () => userIdOption,
            };

            let filters = fp.cloneDeep(fp.get("filters", listProperty));
            if (filters) {
                filters = filters.map((filter) => {
                    const templateString = filter.value.match(/{{:([a-zA-Z0-9_-]+):}}/);
                    if (templateString) {
                        const templateValueFunc = optionsMap[templateString[1]];
                        filter.value = templateValueFunc();
                    }
                    return filter;
                });
            }
            const payload = {
                ...listProperty,
                limit,
                offset,
                filters,
            };
            dispatch(postDashboardQueryList(payload))
                .then((res) => {
                    if (isUnmounted) {
                        return;
                    }
                    const items = fp.get(`result.rows`, res);
                    if (!fp.isEmpty(items)) {
                        setList(fp.concat(array ? array : list, items));
                        limit > items.length && setIsEmptyList(true);
                    } else {
                        setIsEmptyList(true);
                    }
                })
                .finally(() => {
                    if (isUnmounted) {
                        return;
                    }
                    setIsLoading(false);
                });
        },
        [list, limit, offset, state[name], selectedOption, state.activeProjectId],
    );

    const getNextOffset = useCallback(
        (count) => {
            const nextOffset = count * limit;
            setOffset(nextOffset);
            setCount(count);
        },
        [limit],
    );

    const handleReset = useCallback(
        ({ selectedOption, projectId }) => {
            setIsLoading(true);
            setIsEmptyList(false);
            setCount(0);
            setOffset(0);
            setList([]);
            setSelectedOption(selectedOption ? selectedOption : defaultSelectedOption);
            getList(limit, 0, [], selectedOption, projectId ? projectId : state.activeProjectId);
        },
        [selectedOption, state.activeProjectId],
    );

    const handleNext = () => {
        if (fp.isEqual(fp.get(`${name}.result.totalCount`, state), list.length)) {
            return false;
        } else if (!fp.isEmpty(fp.get(`${name}.result.rows`, state)) && !fp.isEmpty(list)) {
            handleDebounceNextOffset(count + 1);
            handleDebounceTrigger(limit, (count + 1) * limit, list, selectedOption, state.activeProjectId);
        }
    };

    const handleDebounceTrigger = useCallback(fp.throttle(300, getList), [getList]);
    const handleDebounceNextOffset = useCallback(fp.throttle(300, getNextOffset), [getNextOffset]);

    useEffect(() => {
        !fp.isEmpty(user) &&
            setOptions([
                { label: t(`common::label::All Users`), value: "ALL" },
                { label: t(`common::label::Only mine`), value: fp.get(`id`, user) },
            ]);
        // 언어 변경 시에도 필터 문구가 따라 바뀌도록 t를 의존성에 포함한다.
    }, [user, t]);

    useEffect(() => {
        return () => setIsUnmounted(false);
    }, []);

    return (
        <WidgetWrap id={name} onMove={onMove}>
            <WidgetHead
                title={title}
                icon={icon}
                color={color}
                name={name}
                removable={removable}
                onDelete={onDelete}
                onMoreView={() => history.push(`${menuService.paths.contents(spaceId, projectId)}/${contentTypeLower}`)}
            />
            <div className={"body"}>
                <div className={"inner"} id={`${name}-scrollable`}>
                    {(isLoading && (
                        <div className={"loading-block"}>
                            <span className={"loading-content"} />
                        </div>
                    )) || (
                        <>
                            <div className="control">
                                <div className="control-status">
                                    <span>{t(`common::label::Total`, "Total")}: </span>
                                    <strong className="text-primary">{list.length}</strong>
                                    {!fp.isEmpty(list) && (
                                        <strong>of {fp.get(`${name}.result.totalCount`, state)}</strong>
                                    )}
                                </div>
                                <div className="control-tools">
                                    <WithSelect
                                        size={"sm"}
                                        defaultValue={fp.get(`value`, selectedOption)}
                                        options={options}
                                        menuPortalTarget={document.body}
                                        onChange={(option) => {
                                            handleReset({ selectedOption: option, projectId: state.activeProjectId });
                                        }}
                                    />
                                    <hr />
                                    <button
                                        className="btn btn-icon-solid"
                                        onClick={() => handleReset({ selectedOption })}
                                    >
                                        <IconRefresh size={16} />
                                    </button>
                                </div>
                            </div>
                            {(fp.isEmpty(list) && (
                                <div className={"empty"}>
                                    <i className={"sprite sprite-nodata"} />
                                    <p>{t(`common::msg::There is no data`)}</p>
                                </div>
                            )) || (
                                <div className="table table-responsive table-responsive-x" id={`${name}-table-wrap`}>
                                    {isMobile ? (
                                        <HorizontalInfinity
                                            scrollableTarget={`${name}-table-wrap`}
                                            dataLength={offset}
                                            next={() => {
                                                handleNext();
                                            }}
                                            hasMore={
                                                !isEmptyList &&
                                                !fp.isEqual(fp.get(`${name}.result.totalCount`, state), list.length)
                                            }
                                            loader={
                                                <div className={"loading-block"}>
                                                    <span className={"loading-content"} />
                                                </div>
                                            }
                                        >
                                            <table className="table table-hover">
                                                <colgroup>
                                                    <col />
                                                    {fp
                                                        .get("columns", listProperty)
                                                        .filter((col) => col === "contentDuration").length > 0 && (
                                                        <col width="70px" />
                                                    )}
                                                    <col width="100px" />
                                                    <col width="150px" />
                                                    <col width="100px" />
                                                </colgroup>
                                                <tbody>
                                                    {fp.map.convert({ cap: false })(
                                                        (item, index) => (
                                                            <tr key={index}>
                                                                <td className="fit-content">
                                                                    <div className="list-content">
                                                                        <div
                                                                            className={`list-thumbnail type-${fp.pipe(
                                                                                fp.get(`contentType`),
                                                                                fp.toLower,
                                                                                (contentType) =>
                                                                                    contentType === "music"
                                                                                        ? "audio"
                                                                                        : contentType,
                                                                            )(item)}`}
                                                                        >
                                                                            <MediaImage
                                                                                mediaType={fp.get(`contentType`, item)}
                                                                                url={fp.get(
                                                                                    `contentThumbnailUrl`,
                                                                                    item,
                                                                                )}
                                                                            />
                                                                        </div>

                                                                        <div className={"list-title"}>
                                                                            <Link
                                                                                to={`${menuService.paths.project(
                                                                                    spaceId,
                                                                                    projectId,
                                                                                )}/contents/${contentTypeLower}/${fp.get(
                                                                                    `contentId`,
                                                                                    item,
                                                                                )}`}
                                                                            >
                                                                                <span className={"text-ellipsis"}>
                                                                                    {fp.get(`contentName`, item)}
                                                                                </span>
                                                                            </Link>
                                                                            <small>{fp.get(`contentId`, item)}</small>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                {fp
                                                                    .get("columns", listProperty)
                                                                    .filter((col) => col === "contentDuration").length >
                                                                    0 && (
                                                                    <td data-title={t(`common::label::${"Duration"}`)}>
                                                                        {formatDuration(
                                                                            fp.get(`contentDuration`, item),
                                                                        )}
                                                                    </td>
                                                                )}
                                                                <td data-title={t(`common::label::${"Status"}`)}>
                                                                    <StatusBadge
                                                                        status={fp
                                                                            .get(`contentStatus`, item)
                                                                            .toUpperCase()}
                                                                    />
                                                                </td>
                                                                <td data-title={t(`common::label::${"Owner"}`)}>
                                                                    <div className="list-content">
                                                                        <div className="list-title">
                                                                            {(fp.get(`contentOwnerUsername`, item) && (
                                                                                <>
                                                                                    <p>
                                                                                        <span className="text-ellipsis">
                                                                                            {fp.get(
                                                                                                `contentOwnerName`,
                                                                                                item,
                                                                                            )}
                                                                                        </span>
                                                                                    </p>
                                                                                    <small>
                                                                                        {fp.get(
                                                                                            `contentOwnerUsername`,
                                                                                            item,
                                                                                        )}
                                                                                    </small>
                                                                                    <TooltipUserInfo
                                                                                        target={item}
                                                                                        id={fp.get(
                                                                                            `contentOwnerId`,
                                                                                            item,
                                                                                        )}
                                                                                    />
                                                                                </>
                                                                            )) ||
                                                                                `-`}
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td
                                                                    className="text-wrap"
                                                                    data-title={t(`common::label::${"Created"}`)}
                                                                >
                                                                    <time>
                                                                        {convertUtcToLocal(
                                                                            fp.get(`contentCreatedAt`, item),
                                                                        )}
                                                                    </time>
                                                                </td>
                                                            </tr>
                                                        ),
                                                        list,
                                                    )}
                                                </tbody>
                                            </table>
                                        </HorizontalInfinity>
                                    ) : (
                                        <InfiniteScroll
                                            dataLength={offset}
                                            scrollableTarget={`${name}-scrollable`}
                                            next={() => {
                                                handleNext();
                                            }}
                                            hasMore={
                                                !isEmptyList &&
                                                !fp.isEqual(fp.get(`${name}.result.totalCount`, state), list.length)
                                            }
                                            loader={
                                                <div className={"loading-block"}>
                                                    <span className={"loading-content"} />
                                                </div>
                                            }
                                        >
                                            <table className="table table-hover">
                                                <colgroup>
                                                    <col />
                                                    {fp
                                                        .get("columns", listProperty)
                                                        .filter((col) => col === "contentDuration").length > 0 && (
                                                        <col width="70px" />
                                                    )}
                                                    <col width="100px" />
                                                    <col width="150px" />
                                                    <col width="100px" />
                                                </colgroup>
                                                <tbody>
                                                    {fp.map.convert({ cap: false })(
                                                        (item, index) => (
                                                            <tr key={index}>
                                                                <td className="fit-content">
                                                                    <div className="list-content">
                                                                        <div
                                                                            className={`list-thumbnail type-${fp.pipe(
                                                                                fp.get(`contentType`),
                                                                                fp.toLower,
                                                                                (contentType) =>
                                                                                    contentType === "music"
                                                                                        ? "audio"
                                                                                        : contentType,
                                                                            )(item)}`}
                                                                        >
                                                                            <MediaImage
                                                                                mediaType={fp.get(`contentType`, item)}
                                                                                url={fp.get(
                                                                                    `contentThumbnailUrl`,
                                                                                    item,
                                                                                )}
                                                                            />
                                                                        </div>

                                                                        <div className={"list-title"}>
                                                                            <Link
                                                                                to={`${menuService.paths.project(
                                                                                    spaceId,
                                                                                    projectId,
                                                                                )}/contents/${contentTypeLower}/${fp.get(
                                                                                    `contentId`,
                                                                                    item,
                                                                                )}`}
                                                                            >
                                                                                <span className={"text-ellipsis"}>
                                                                                    {fp.get(`contentName`, item)}
                                                                                </span>
                                                                            </Link>
                                                                            <small>{fp.get(`contentId`, item)}</small>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                {fp
                                                                    .get("columns", listProperty)
                                                                    .filter((col) => col === "contentDuration").length >
                                                                    0 && (
                                                                    <td data-title={t(`common::label::${"Duration"}`)}>
                                                                        {formatDuration(
                                                                            fp.get(`contentDuration`, item),
                                                                        )}
                                                                    </td>
                                                                )}
                                                                <td data-title={t(`common::label::${"Status"}`)}>
                                                                    <StatusBadge
                                                                        status={fp
                                                                            .get(`contentStatus`, item)
                                                                            .toUpperCase()}
                                                                    />
                                                                </td>
                                                                <td data-title={t(`common::label::${"Owner"}`)}>
                                                                    <div className="list-content">
                                                                        <div className="list-title">
                                                                            {(fp.get(`contentOwnerUsername`, item) && (
                                                                                <>
                                                                                    <p>
                                                                                        <span className="text-ellipsis">
                                                                                            {fp.get(
                                                                                                `contentOwnerName`,
                                                                                                item,
                                                                                            )}
                                                                                        </span>
                                                                                    </p>
                                                                                    <small>
                                                                                        {fp.get(
                                                                                            `contentOwnerUsername`,
                                                                                            item,
                                                                                        )}
                                                                                    </small>
                                                                                    <TooltipUserInfo
                                                                                        target={item}
                                                                                        id={fp.get(
                                                                                            `contentOwnerId`,
                                                                                            item,
                                                                                        )}
                                                                                    />
                                                                                </>
                                                                            )) ||
                                                                                `-`}
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td
                                                                    className="text-wrap"
                                                                    data-title={t(`common::label::${"Created"}`)}
                                                                >
                                                                    <time>
                                                                        {convertUtcToLocal(
                                                                            fp.get(`contentCreatedAt`, item),
                                                                        )}
                                                                    </time>
                                                                </td>
                                                            </tr>
                                                        ),
                                                        list,
                                                    )}
                                                </tbody>
                                            </table>
                                        </InfiniteScroll>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </WidgetWrap>
    );
};

RecentContents.propTypes = {
    user: PropTypes.object,
    name: PropTypes.string,
    title: PropTypes.string,
    icon: PropTypes.node,
    onMove: PropTypes.func,
    onDelete: PropTypes.func,
    removable: PropTypes.bool,
    query: PropTypes.object,
    contentType: PropTypes.string,
};

export default RecentContents;
